import React from 'react';
import { ArrowLeft, Flame, Brain, Heart, Hourglass, Moon, Droplet, Activity, Smile } from 'lucide-react';
import PreviewWaitlist from './PreviewWaitlist';

/** Calmly-worded list of features each preview will eventually deliver. */
const PREVIEW_ROADMAP = {
  sexual: {
    topic: 'Sexual & Intimate Health',
    accentClass: 'text-orange-600',
    upcoming: [
      'Cycle-aware check-ins for libido, comfort, and intimacy',
      'Vaginal & pelvic-floor wellness guidance',
      'Calm reproductive planning support (when you choose to)',
      'Verified clinician answers to common concerns',
    ],
  },
  mental: {
    topic: 'Mental Wellness',
    accentClass: 'text-purple-600',
    upcoming: [
      'Daily mood + stress check-ins, calmly designed',
      'Personalised wind-down rituals and pause prompts',
      'Cycle / hormonal mood pattern awareness',
      'Optional therapist-vetted reading and audio',
    ],
  },
  heart: {
    topic: 'Heart & General Health',
    accentClass: 'text-red-500',
    upcoming: [
      'Blood-pressure and resting-heart-rate logging',
      'Metabolic insights from sleep, activity, and meals',
      'Reminders for age-appropriate cardiac screenings',
      'Family-history-aware preventive nudges',
    ],
  },
  'life-stages': {
    topic: 'Life Stage Guidance',
    accentClass: 'text-indigo-600',
    upcoming: [
      'Life-stage-specific preventive checklists (early adulthood → menopause)',
      'Calm perimenopause and menopause companion',
      'Bone, heart, and pelvic health priorities by decade',
      'Supportive screening reminders, never alarming',
    ],
  },
};

/**
 * Women's Health preview screens for unbuilt categories. Each preview reads
 * as a calm, intentional snapshot rather than a stub — pulling from today's
 * daily-tracker data and pairing it with calm educational observations.
 *
 * Topics:
 *   - sexual       intimate & reproductive wellbeing
 *   - mental       stress, mood, emotional health
 *   - heart        cardiovascular & metabolic
 *   - life-stages  early adulthood → menopause guidance
 */

const FACTOR_LABEL = {
  water: 'hydration',
  meals: 'nutrition',
  steps: 'movement',
  sleep: 'sleep',
};

const completedSet = (tracker) => new Set((tracker?.tasks_completed) || []);

const PREVIEW_DEFINITIONS = {
  sexual: {
    title: 'Sexual & Intimate Health',
    icon: Flame,
    accent: 'from-orange-50 to-rose-50 border-orange-100',
    iconWrap: 'bg-orange-100 text-orange-700',
    buildInsight: (tracker) => {
      const done = completedSet(tracker);
      const sleepDone = done.has('sleep');
      const stressFreeShape = sleepDone && done.has('water');
      let summary;
      if (stressFreeShape) {
        summary =
          "Your body's signals look balanced today. Sleep and hydration both support libido and intimate wellbeing.";
      } else if (!sleepDone) {
        summary =
          "Sleep is still pending today — recovery is one of the strongest signals for intimate energy and balance.";
      } else {
        summary =
          "Your overall balance shapes intimate wellbeing. Small steady steps today support tomorrow's rhythm.";
      }
      return {
        summary,
        observations: [
          { icon: Moon, text: "Restful sleep is foundational — it shapes hormonal rhythm and natural desire." },
          { icon: Droplet, text: "Hydration supports vaginal health and overall comfort across the cycle." },
          { icon: Smile, text: "Stress and mood directly affect intimacy — a gentle day for the body is a gentle day for connection." },
        ],
      };
    },
  },
  mental: {
    title: 'Mental & Emotional Health',
    icon: Brain,
    accent: 'from-purple-50 to-indigo-50 border-purple-100',
    iconWrap: 'bg-purple-100 text-purple-700',
    buildInsight: (tracker) => {
      const done = completedSet(tracker);
      const sleepDone = done.has('sleep');
      const movementDone = done.has('steps');
      let summary;
      if (sleepDone && movementDone) {
        summary =
          "Your foundations are in a good place today — sleep and movement both lift mood and emotional steadiness.";
      } else if (!sleepDone) {
        summary =
          "Sleep is still pending — it's one of the most powerful regulators of mood and emotional balance.";
      } else {
        summary =
          "A gentle, balanced day for the body usually settles into a steadier mind. Keep things simple today.";
      }
      return {
        summary,
        observations: [
          { icon: Moon, text: "7–8 hours of sleep is one of the strongest signals for mood and resilience." },
          { icon: Activity, text: "Even short, gentle movement releases mood-supporting endorphins." },
          { icon: Smile, text: "Naming what you feel — without judgement — is the first step in any calm day." },
        ],
      };
    },
  },
  heart: {
    title: 'Heart & General Health',
    icon: Heart,
    accent: 'from-red-50 to-rose-50 border-red-100',
    iconWrap: 'bg-red-100 text-red-700',
    buildInsight: (tracker) => {
      const done = completedSet(tracker);
      const movementDone = done.has('steps');
      const mealsDone = done.has('meals');
      let summary;
      if (movementDone && mealsDone) {
        summary =
          "Your heart fundamentals look good today — daily movement and balanced meals are the most evidence-backed combination.";
      } else if (!movementDone) {
        summary =
          "Movement is still pending today. Even 10 minutes of walking is one of the best gifts you can give your heart.";
      } else {
        summary =
          "Steady habits shape long-term cardiovascular health more than any single perfect day.";
      }
      return {
        summary,
        observations: [
          { icon: Activity, text: "Daily movement of any intensity is the strongest single predictor of heart health." },
          { icon: Heart, text: "Balanced meals — vegetables, lean protein, whole grains — protect blood pressure long term." },
          { icon: Moon, text: "Quality sleep regulates blood pressure and inflammation. Both compound over years." },
        ],
      };
    },
  },
  'life-stages': {
    title: 'Life Stage Guidance',
    icon: Hourglass,
    accent: 'from-indigo-50 to-purple-50 border-indigo-100',
    iconWrap: 'bg-indigo-100 text-indigo-700',
    buildInsight: () => ({
      summary:
        "Health priorities shift gently with each life stage — from cycle awareness in early adulthood, through pregnancy and fertility, to menopause and beyond.",
      observations: [
        { icon: Smile, text: "Early adulthood — build calm habits around cycle, mood, and movement." },
        { icon: Heart, text: "Pregnancy & fertility — focus on nutrition, prenatal care, and emotional support." },
        { icon: Hourglass, text: "Perimenopause & menopause — bone health, heart health, and sleep matter more than ever." },
      ],
    }),
  },
};

const buildPersonalContext = (tracker) => {
  const done = completedSet(tracker);
  const pending = ['water', 'meals', 'steps', 'sleep']
    .filter((k) => !done.has(k))
    .map((k) => FACTOR_LABEL[k]);
  if (pending.length === 0) return null;
  if (pending.length === 1) return `Today's signal: your ${pending[0]} is the only piece still pending.`;
  return `Today's signal: your ${pending[0]} and ${pending[1]} are pulling on the same thread.`;
};

const WomensHealthPreview = ({ previewKey, tracker, onBack }) => {
  const def = PREVIEW_DEFINITIONS[previewKey];
  if (!def) return null;

  const Icon = def.icon;
  const { summary, observations } = def.buildInsight(tracker);
  const context = previewKey === 'life-stages' ? null : buildPersonalContext(tracker);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-pink-50/40 via-white to-white pb-24"
      data-testid={`womens-health-preview-${previewKey}`}
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-5"
          data-testid="womens-health-preview-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Women's Health
        </button>

        <div className={`rounded-2xl border bg-gradient-to-br ${def.accent} p-6 shadow-sm mb-5`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${def.iconWrap}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">
                Preview
              </p>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">{def.title}</h1>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                This feature is evolving — here's a quick preview from your daily check-in.
              </p>
            </div>
          </div>
        </div>

        <div
          className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4"
          data-testid={`womens-health-preview-${previewKey}-summary`}
        >
          <p className="text-[11px] font-semibold text-pink-700 uppercase tracking-widest mb-2">
            {previewKey === 'life-stages' ? 'A gentle map' : "Today's snapshot"}
          </p>
          <p className="text-base text-gray-900 font-medium leading-relaxed">{summary}</p>
          {context && <p className="text-sm text-gray-500 mt-2 leading-relaxed">{context}</p>}
        </div>

        <div className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3">
            What we know so far
          </p>
          <ul className="space-y-3">
            {observations.map((o) => {
              const Oi = o.icon;
              return (
                <li key={o.text} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0">
                    <Oi className="h-3.5 w-3.5 text-gray-600" />
                  </span>
                  <p className="text-sm text-gray-700 leading-relaxed">{o.text}</p>
                </li>
              );
            })}
          </ul>
        </div>

        {PREVIEW_ROADMAP[previewKey] && (
          <PreviewWaitlist
            topic={PREVIEW_ROADMAP[previewKey].topic}
            source={`womens-health-${previewKey}`}
            upcoming={PREVIEW_ROADMAP[previewKey].upcoming}
            accentClass={PREVIEW_ROADMAP[previewKey].accentClass}
          />
        )}

        <p className="text-xs text-gray-400 mt-6 text-center">Personalised insights deepen as you keep checking in.</p>
      </div>
    </div>
  );
};

export default WomensHealthPreview;
